import type { ContentRegistry } from '../content/registry';
import type { BossDef, EmitterDef, WeaponFireDef } from '../content/types';
import type { EventQueue } from '../core/events';
import type { Rng } from '../core/rng';
import type { Stats } from './stats';
import type { BurstContext } from './systems/projectiles';
import { wrapDelta, type World } from './world';
import type { CharacterId } from '../content/characters';
import type { SkillFlag } from '../content/skills';

/** Everything systems need, passed explicitly (no globals → testable, snapshot-able). */
export interface SimCtx {
  w: World;
  reg: ContentRegistry;
  /** Gameplay rng (spawns, patterns, crits). */
  rng: Rng;
  events: EventQueue;
  /** Per-player resolved stats (recomputed when upgrades/equipment change). */
  stats: Stats[];
  /** Per-player equipped weapon fire definition. */
  weapons: WeaponFireDef[];
  /** Cached derived pattern durations by registry pattern index. */
  patternDurations: Float32Array;
  /** Scaling applied to enemies at spawn (stage/era/coop/paradox). */
  scale: { hp: number; damage: number; speed: number; xp: number; bossHp: number; hazardDamage: number };
  /** Active boss (if any). */
  boss: { def: BossDef; slot: number } | null;
  playerCount: number;
  /** Character per player; drives signature abilities. */
  characters: CharacterId[];
  /** Validated character keystones per player. */
  skillFlags: ReadonlySet<SkillFlag>[];
  /** Debug: players take no damage. */
  godMode: boolean;
  /** Running totals (all sources, including burn/hazard kills). */
  kills: number;
  bossKills: number;
  /** Objective hook receives deterministic enemy identity before the entity is released. */
  objectiveKill: ((enemyId: string, elite: boolean) => void) | null;
  _chainVisited: Uint8Array;

  // --- scratch state reused across calls (never allocated per tick) ---
  _np: { idx: number; dx: number; dy: number; d2: number };
  _cluster: Float32Array;
  _clusterSet: boolean;
  _pos: Float32Array;
  _burst: BurstContext;
  _weaponEmitter: EmitterDef;
}

export function nearestPlayer(ctx: SimCtx, x: number, y: number): { idx: number; dx: number; dy: number; d2: number } {
  const out = ctx._np;
  out.idx = -1;
  out.d2 = 1e12;
  out.dx = 0;
  out.dy = 0;
  for (let i = 0; i < ctx.w.players.length; i++) {
    const p = ctx.w.players[i];
    if (!p.alive) continue;
    const half = ctx.w.half;
    const dx = wrapDelta(p.x - x, half);
    const dy = wrapDelta(p.y - y, half);
    const d2 = dx * dx + dy * dy;
    if (d2 < out.d2) {
      out.d2 = d2;
      out.dx = dx;
      out.dy = dy;
      out.idx = i;
    }
  }
  return out;
}
